// Адрес раздела «Суточные расходы» (адрес `/money`, до 01.10.2026 раздел назывался «Деньги»).
// Правило 4: в адресе кодируются раздел, представление, отбор, строка, вкладка карточки и
// открытая панель — ссылку коллега открывает в том же состоянии.
//
//   /money/accountable                          пульт подотчёта (стартовый экран финдиректора)
//   /money/accountable?f.entity=<id>            отбор по юрлицу
//   /money/accountable?f.over=true              только те, у кого выше порога
//   /money/accountable/r/<id>?tab=claims        карточка лица, вкладка «Заявки»
//   /money/claims                               очередь приёмки
//   /money/claims?f.receipt=none                только расходы без чека
//   /money/claims?f.min=500000                  сумма от 5 000 ₽
//   /money/issues?f.period=2026-05              «Кому выдано»: выдачи за май
//   /money/flow?f.period=2026-05                «На что потрачено»: расход за май со сверкой
//   /money/archive                              архив переписки
//   ?p=issue:new                                мастер «Оформить выдачу» поверх раздела
//   ?p=claim:<id>                               одна заявка панелью
//
// Чистые функции без React: их гоняют тесты и переиспользуют все экраны раздела.

import { buildPath } from '@/shared/routing/paths';
import { buildSearch, EMPTY_SEARCH, pushPanel, type SearchState } from '@/shared/routing/search';

export const SECTION = 'money';

/** Пульт: люди с деньгами на руках. Он же стартовый экран финансового директора. */
export const VIEW_DESK = 'accountable';
/** Очередь приёмки заявок. */
export const VIEW_CLAIMS = 'claims';
/** Куда ушли деньги в полях: принятые заявки по категориям за месяц, со сверкой. «На что потрачено». */
export const VIEW_FLOW = 'flow';
/**
 * «Кому выдано»: реестр выдач за месяц и их сумма.
 *
 * Тз 5.6 обещало «реестр выдач и сводку по категориям (аналог листа «итог» Заказчика)». Сводка
 * по категориям была («На что потрачено»), реестра выдач за период не было. Месяц в адресе
 * (`f.period`) общий с «На что потрачено»: это две половины одного вопроса, «что дали и куда
 * потратили».
 */
export const VIEW_ISSUES = 'issues';
/**
 * Архив переписки: четыре года Вотсапа заказчика с поиском.
 *
 * Стоит в «Суточных расходах», а не отдельным разделом: разделов в продукте семь и восьмой не заводится
 * (ПРАВИЛА.md, раздел 1.1), а спрашивают у архива денежное — «сколько я выдал Позднякову за
 * два года», «на какую карту переводили в марте».
 */
export const VIEW_ARCHIVE = 'archive';

export type MoneyView =
  | typeof VIEW_DESK
  | typeof VIEW_CLAIMS
  | typeof VIEW_ISSUES
  | typeof VIEW_FLOW
  | typeof VIEW_ARCHIVE;

/**
 * Представления, которые показывают один месяц и листают его стрелками.
 *
 * Между ними месяц переносится при смене вкладки: директор смотрит май на «Кому выдано» и
 * нажимает «На что потрачено», ожидая увидеть тот же май, а не сегодняшний месяц.
 */
export const MONTHLY_VIEWS: readonly MoneyView[] = [VIEW_ISSUES, VIEW_FLOW];

export const DEFAULT_VIEW: MoneyView = VIEW_DESK;

/** Разбор слага из адреса. null — такого представления в разделе нет. */
export function parseView(raw: string | undefined): MoneyView | null {
  if (raw === undefined || raw === VIEW_DESK) {
    return VIEW_DESK;
  }
  if (raw === VIEW_CLAIMS) {
    return VIEW_CLAIMS;
  }
  if (raw === VIEW_ISSUES) {
    return VIEW_ISSUES;
  }
  if (raw === VIEW_FLOW) {
    return VIEW_FLOW;
  }
  return raw === VIEW_ARCHIVE ? VIEW_ARCHIVE : null;
}

// ── Ключи отбора ──────────────────────────────────────────────────────────────────────────
// Голая строка в разметке разъезжается с разбором адреса, поэтому ключи здесь константами.

export const FILTER_ENTITY = 'entity';
export const FILTER_OVER_LIMIT = 'over';
export const FILTER_RECEIPT = 'receipt';
export const FILTER_MIN_AMOUNT = 'min';
/** Только круглые суммы: ?f.round=on. Тз, раздел 5.6, «справочный фильтр». */
export const FILTER_ROUND = 'round';
export const ROUND_ON = 'on';
export const FILTER_CATEGORY = 'category';
export const FILTER_PERSON = 'person';
/** Месяц, за который показывают выдачи и расход. Ключ тот же, что в «Расчёте»: адреса
 * разделов читает один человек, и «period» в одном месте против «month» в другом он запомнить
 * не обязан. */
export const FILTER_PERIOD = 'period';
/** «Действующие» или «с учётом сторно». Правило 8: переключатель у каждого денежного списка. */
export const FILTER_REVERSED = 'reversed';

/** Чат выгрузки в архиве переписки: ?f.chat=Авансы. */
export const FILTER_ARCHIVE_CHAT = 'chat';
/** Автор в архиве переписки, строкой из телефонной книги: ?f.author=Папка. */
export const FILTER_ARCHIVE_AUTHOR = 'author';
/**
 * Границы дат в архиве: ?f.from=2026-03-01&f.to=2026-03-31.
 *
 * Не `period`, как у месячных отчётов: там календарь листается помесячно, а здесь спрашивают
 * «с марта по август» — период у архива произвольный и обеими границами необязательный.
 */
export const FILTER_FROM = 'from';
export const FILTER_TO = 'to';

/** Значение отбора по чеку: только расходы без подтверждения. */
export const RECEIPT_NONE = 'none';
/** Значение отбора по сторно: показывать и сторнированные, перечёркнутыми. */
export const REVERSED_WITH = 'with';

/** Порядок на пульте подотчёта. В адресе, а не в состоянии компонента: пульт присылают
 *  ссылкой («посмотри, у кого висит»), и порядок обязан приезжать вместе с ней. */
export const FILTER_ORDER = 'order';
/** По сумме: «где больше всего». Умолчание — по давности, и его в адрес не пишем. */
export const ORDER_BY_AMOUNT = 'amount';
/** По давности: «кто дольше всех не отчитывается». Умолчание сервера. */
export const ORDER_BY_AGE = 'age';

// ── Вкладки карточки ──────────────────────────────────────────────────────────────────────

export const TABS = ['overview', 'issues', 'claims', 'transfers', 'returns'] as const;
export type PersonTab = (typeof TABS)[number];
export const DEFAULT_TAB: PersonTab = 'overview';

export function parseTab(raw: string | undefined): PersonTab {
  return raw !== undefined && (TABS as readonly string[]).includes(raw)
    ? (raw as PersonTab)
    : DEFAULT_TAB;
}

// ── Виды панелей ──────────────────────────────────────────────────────────────────────────

export const PANEL_ISSUE = 'issue';
export const PANEL_RETURN = 'return';
export const PANEL_TRANSFER = 'transfer';
export const PANEL_NEW_CLAIM = 'new-claim';
export const PANEL_CLAIM = 'claim';
/** Настройки подотчёта лица: порог подсветки и действующая подотчётность (тз, раздел 5.5). */
export const PANEL_PERSON_SETTINGS = 'person-settings';
/**
 * Сводка за период панелью.
 *
 * Нужна только на узком экране: на компьютере те же четыре числа стоят правой колонкой пульта
 * и открываются без единого нажатия. В книжной ориентации планшета правой колонки нет —
 * поверхность одна, и её занимает список людей с деньгами, — а числа обязаны остаться
 * достижимыми, иначе планшет теряет против компьютера целый экран.
 */
export const PANEL_SUMMARY = 'summary';

// ── Сборка адресов ────────────────────────────────────────────────────────────────────────

export function viewPath(view: MoneyView, search?: SearchState): string {
  return buildPath({ section: SECTION, view }) + buildSearch(search ?? EMPTY_SEARCH);
}

export function personPath(personId: string, search?: SearchState): string {
  return (
    buildPath({ section: SECTION, view: VIEW_DESK, rowId: personId }) +
    buildSearch(search ?? EMPTY_SEARCH)
  );
}

/** Открыть панель поверх текущего состояния, не уводя человека из раздела. */
export function withPanel(search: SearchState, view: string, id: string): SearchState {
  return pushPanel(search, { view, id });
}

/**
 * Что переносится при смене представления.
 *
 * Юрлицо и отбор по человеку переносятся: «покажи заявки вот этого» — продолжение той же
 * мысли, а не новый вопрос. Вкладка карточки, поиск и отбор по чеку не переносятся: они
 * относятся к конкретному списку, и в другом означали бы уже другое.
 *
 * Месяц переносится только по просьбе (`месяц`): между «Кому выдано» и «На что потрачено» —
 * да, в остальные вкладки — нет, там он ничего не значит и только засорял бы адрес.
 */
export function carryFilters(search: SearchState, опции: { месяц?: boolean } = {}): SearchState {
  const перенос: Record<string, string> = {};
  const юрлицо = search.filters[FILTER_ENTITY];
  const человек = search.filters[FILTER_PERSON];
  const месяц = search.filters[FILTER_PERIOD];
  if (юрлицо !== undefined) {
    перенос[FILTER_ENTITY] = юрлицо;
  }
  if (человек !== undefined) {
    перенос[FILTER_PERSON] = человек;
  }
  if (опции.месяц === true && месяц !== undefined) {
    перенос[FILTER_PERIOD] = месяц;
  }
  return { ...EMPTY_SEARCH, filters: перенос };
}
